import React, { useEffect, useMemo, useRef, useState } from 'react';
import FormInput from '../YogaUI/FormInput';
import {
  IS_CHECKOUT_PAGE,
  IS_PRESUMABLY_EXISTING_CUSTOMER,
  MAX_NAME_LENGTH,
  globalWindow,
} from '../../../../utils/constants';
import { phoneNumberFormatter } from '../SignInModal/utils';
import './styles.scss';
import { fireHeapCustomerCreateEvent, fireHeapCustomerLoginEvent } from '../../../../utils/heap-events';
import { ShippingCountryState } from '../../../../utils/shipping';
import { useDidUpdateEffect, usePromise } from '../../react-hooks';
import { isInGeotarget } from '../../../../utils/location';
import { SignInModalTab } from '../SignInModal/types';
import { getLoyaltyBlockedStatus } from '../../loyaltyBlockedStatus';
import { GWPandLoyaltyGeoSettings } from 'src/scripts/globals';
import { addRsvpTag, RSVP_COMMUNITY_TAG } from 'src/scripts/utils/rsvp-events';
import { Alert } from '@material-ui/lab';
import { InitialUrlSearchParams, isLocal } from 'src/scripts/utils/environment';
import InputMask from 'react-input-mask';
import { applyDiscountCode } from 'src/scripts/utils/autoCouponApplicator';
import { prependLocaleToPath, prependLocaleToUrl } from '../../../../utils/localization';
import FaceIdButton from '../FaceIdEnableConfirmModal/FaceIdButton';
import FACE_ID from 'src/scripts/utils/faceid-helpers';
import ErrorBoundary from 'src/scripts/utils/react/ErrorBoundary';
import { handleFormEvents, pushEventToGTMDataLayer } from 'src/scripts/utils/gtm-data-layer';
import Checkbox from '../YogaUI/Checkbox';
import { triggerMergeCartOnLogin } from 'src/scripts/utils/cart/cart-apis';
import ButtonWithProgress from '../YogaUI/ButtonWithProgress';
import sanitizeHtml from 'sanitize-html';
import { recordCustomEventToNewRelic } from '../../../../utils/helpers';
import { aloApiFetch } from 'src/scripts/utils/alo-api-fetch';

const defaultTab = IS_PRESUMABLY_EXISTING_CUSTOMER ? 'sign-in' : 'create-account';
type CustomerStateLookupStatus = 'activation-required' | 'non-activation' | 'error';
const ACTIVATION_EMAIL_SENT_COPY_PREFIX = 'Click the link in the email sent to';
const ACCOUNT_STATUS_LOOKUP_ERROR_MESSAGE = 'Unable to verify your account status right now. Please try again.';
type FormAlertType = 'success' | 'error';

const AuthForm = ({
  returnUrl,
  defaultTabOverride,
  isSignInModal = false,
  onTabSwitchCallback,
  trackingOptions,
  prePopulatedEmail: inputPrePopulatedEmail,
  useCustomHero = false,
}: {
  returnUrl: string | undefined;
  defaultTabOverride?: SignInModalTab;
  isSignInModal?: boolean;
  onTabSwitchCallback?: (tab: SignInModalTab) => void;
  trackingOptions?: { location: string };
  prePopulatedEmail?: string;
  useCustomHero?: boolean;
}) => {
  const [formType, setFormType] = useState<SignInModalTab>(defaultTabOverride || defaultTab);
  const [recoveryPrefillEmail, setRecoveryPrefillEmail] = useState<string | null>(null);
  const [isCheckingCustomerState, setIsCheckingCustomerState] = useState(false);
  const prePopulatedEmail = recoveryPrefillEmail || FACE_ID.prePopulateFaceIDEmail(formType) || inputPrePopulatedEmail;

  const [customerEmailState, setCustomerEmailState] = useState(!!prePopulatedEmail || false);
  const [customerPasswordState, setCustomerPasswordState] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumberError, setPhoneNumberErrorState] = useState(false);
  const [birthdayError, setBirthdayErrorState] = useState({ shouldShowErrorUI: false, hasValidValue: true });
  const [resetPasswordEmail, setResetPasswordEmail] = useState('your email');
  const [heroVisibility, setHeroVisibility] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const phoneNumberTooltipMessage = 'Enables faster account lookup in-store.';
  const activeFormRef = useRef<HTMLFormElement>(null);

  const { data: isInUsGeoInfo, isLoading } = usePromise(() => isInGeotarget({ countries: ['US'] }));
  const isInUS = ShippingCountryState.countryCode ? ShippingCountryState.countryCode === 'US' : isInUsGeoInfo;

  const [formState, setFormState] = useState({
    FirstName: false,
    LastName: false,
    Email: prePopulatedEmail || false,
    CreatePassword: false,
    RecoverEmail: prePopulatedEmail || false,
  });
  const signInModalSettings = window.getSignInModalSettings?.();
  const signInCta = useMemo(
    () => (isInUS ? signInModalSettings?.signInCta : signInModalSettings?.internationalSignInCta),
    [signInModalSettings, isInUS]
  );

  const discountCodeToApplyOnSignIn = isLoading
    ? ''
    : !isInUS
    ? signInModalSettings?.internationalDiscountCode
    : signInModalSettings?.discountCode;

  const createAccountCta = useMemo(
    () => (isInUS ? signInModalSettings?.createAccountCta : signInModalSettings?.internationalCreateAccountCta),
    [signInModalSettings, isInUS]
  );
  const _returnUrl = useMemo(() => {
    const urlParams = InitialUrlSearchParams;
    const checkoutUrl = urlParams.get('checkout_url') || (IS_CHECKOUT_PAGE && window.location.href);

    if (checkoutUrl) {
      return checkoutUrl;
    } else {
      let urlObject: URL;
      try {
        urlObject = parseUrl(returnUrl || window.location.href);
      } catch (err) {
        console.error(err);
        urlObject = parseUrl(window.location.href);
      }

      urlObject.searchParams.set('auth_success', '1');
      urlObject.searchParams.set('is_modal', isSignInModal.toString());

      // ? [REVISIT] Can this be replaced with the more effecient 'applyDiscountCode'?
      // - only question/problem is that social login we don't have a hook into & we still need to generate the URL
      const finalUrl =
        discountCodeToApplyOnSignIn && discountCodeToApplyOnSignIn !== ''
          ? prependLocaleToPath(
              `/discount/${discountCodeToApplyOnSignIn}?redirect=${encodeURIComponent(
                prependLocaleToPath(urlObject.pathname)
              )}&${urlObject.searchParams}`
            )
          : prependLocaleToUrl(urlObject.toString());

      return finalUrl;
    }
  }, [returnUrl, isSignInModal, discountCodeToApplyOnSignIn]);

  useEffect(() => {
    onTabSwitchCallback && onTabSwitchCallback(formType);
  }, [formType, onTabSwitchCallback]);

  useEffect(() => {
    const originalReturnUrl = window.sessionStorage.getItem('social-login-return-url');
    window.sessionStorage.setItem('social-login-return-url', _returnUrl + '&auth_event=login&social_login=true');
    return () => {
      if (originalReturnUrl) {
        window.sessionStorage.setItem(
          'social-login-return-url',
          // ? Unrelated to this PR, but how does this work, we are adding `?` w/o checking if it already exists
          originalReturnUrl + '?auth_success=1&auth_event=login&social_login=true'
        );
      } else {
        window.sessionStorage.removeItem('social-login-return-url');
      }
    };
  }, [_returnUrl]);

  useEffect(() => {
    if (formType === 'create-account' || formType === 'sign-in') {
      const getUserStatus = async () => {
        const loyaltySettings = await GWPandLoyaltyGeoSettings();
        const isLoyaltyEnabled =
          loyaltySettings.shouldRenderLoyaltyForDomestic || loyaltySettings.shouldRenderLoyaltyWidgetForINTL;
        const isBlocked = await getLoyaltyBlockedStatus();
        const shouldRenderLoyalty = !isBlocked && isLoyaltyEnabled;
        setHeroVisibility(shouldRenderLoyalty);
      };
      getUserStatus();
    } else {
      setHeroVisibility(false);
    }
  }, [formType]);

  const heroImageUrl = useMemo(() => {
    if (formType === 'sign-in') {
      return isInUS ? signInModalSettings?.signInHeroImage : signInModalSettings?.internationalSignInHeroImage;
    } else if (formType === 'create-account') {
      return isInUS
        ? signInModalSettings?.createAccountHeroImage
        : signInModalSettings?.internationalCreateAccountHeroImage;
    }
  }, [formType, signInModalSettings, isInUS]);

  const FormAlert = ({ errorMessage, alertType }: { errorMessage: string; alertType: FormAlertType }) => {
    const errorRef = useRef<HTMLDivElement>(null);
    const containsResetLink = errorMessage.includes('reset your password');

    // just clicking the href in the error message won't show the reset form, we need to set the form state
    useEffect(() => {
      if (containsResetLink) {
        const link = errorRef.current?.querySelector('a[href*="recover"]');
        if (link) {
          const handleClick = (e: Event) => {
            e.preventDefault();
            setFormType('recovery');
          };
          link.addEventListener('click', handleClick);
          return () => {
            link.removeEventListener('click', handleClick);
          };
        }
      }
    }, [errorMessage, containsResetLink]);

    return (
      <Alert severity={alertType} ref={errorRef} style={{ marginBottom: 20 }}>
        <span dangerouslySetInnerHTML={{ __html: errorMessage }}></span>
      </Alert>
    );
  };

  // ? of note: these `Submittable` variables are unreliable since the forms/inputs are uncontrolled inputs
  // https://react.dev/learn/sharing-state-between-components#controlled-and-uncontrolled-components
  const isLoginFormSubmittable = useMemo(() => {
    return customerEmailState && customerPasswordState;
  }, [customerEmailState, customerPasswordState]);

  const isAccountFormSubmittable = useMemo(() => {
    return (
      formState.FirstName &&
      formState.LastName &&
      formState.Email &&
      formState.CreatePassword &&
      birthdayError.hasValidValue
    );
  }, [formState.FirstName, formState.LastName, formState.Email, formState.CreatePassword, birthdayError.hasValidValue]);

  const isResetPasswordSubmittable = useMemo(() => {
    return formState.RecoverEmail;
  }, [formState.RecoverEmail]);
  const isActivateAccountForm = formType === 'activate-account';

  const FormTypeSwitch = (
    <div className="auth-form-tabs" style={{ cursor: 'pointer', borderBottom: '1px solid var(--alto)' }}>
      <div
        className={`auth-form-tab h6 bold ${formType === 'create-account' ? 'active' : ''}`}
        onClick={() => setFormType('create-account')}
      >
        Join Now
      </div>
      <div
        className={`auth-form-tab h6 bold ${formType === 'sign-in' ? 'active' : ''}`}
        onClick={() => setFormType('sign-in')}
      >
        Sign In
      </div>
    </div>
  );

  const heroBlock = useCustomHero ? (
    <div className="auth-form-hero">
      <img
        src="https://cdn.shopify.com/s/files/1/2185/2813/files/alo_access_logo3d.png?v=1727455741"
        height="37"
        alt="aloaccess"
      />
      <p>
        {formType === 'create-account' ? (
          <>
            Join today for free and get points on every purchase, first dibs on drops, member-only benefits & so much
            more.
          </>
        ) : (
          <>You’ve already started earning–sign in to see your status.</>
        )}
      </p>
    </div>
  ) : !heroImageUrl ? (
    <></>
  ) : (
    <div
      className="account__hero"
      style={{
        backgroundImage: `url("${heroImageUrl}")`,
      }}
    ></div>
  );

  const [formError, setFormError] = useState<FormError | null>(null);

  const checkCustomerStateFromAloApi = async (
    email: string
  ): Promise<{
    status: CustomerStateLookupStatus;
    errorMessage?: string;
  }> => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      return { status: 'non-activation' };
    }
    const startTime = performance.now();

    try {
      const response = await aloApiFetch(`/v1/account/status?email=${encodeURIComponent(normalizedEmail)}`);

      if (!response.ok) {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        recordCustomEventToNewRelic('account_status_lookup_error', {
          fetchDuration,
          userAgent: window.navigator.userAgent,
          date: Date.now(),
          pathname: window.location.pathname,
          statusCode: response.status,
          statusText: response.statusText,
          message: 'Alo API account status lookup returned non-OK response',
        });
        return {
          status: 'error',
          errorMessage: ACCOUNT_STATUS_LOOKUP_ERROR_MESSAGE,
        };
      }

      const payload = await response.json();
      if (payload?.requires_activation_view === true) {
        return { status: 'activation-required' };
      }

      return { status: 'non-activation' };
    } catch (error) {
      const endTime = performance.now();
      const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      recordCustomEventToNewRelic('account_status_lookup_error', {
        fetchDuration,
        userAgent: window.navigator.userAgent,
        date: Date.now(),
        pathname: window.location.pathname,
        message: errorMessage,
      });
      console.error('Failed to lookup customer state via alo-api', error);
      return {
        status: 'error',
        errorMessage: ACCOUNT_STATUS_LOOKUP_ERROR_MESSAGE,
      };
    }
  };

  const submitResetPassword = async (event: React.FormEvent) => {
    if (isCheckingCustomerState) return;
    const startTime = performance.now();
    setFormError(null);
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const email = formData.get('email')?.toString() || '';

    setIsCheckingCustomerState(true);
    const customerState = await checkCustomerStateFromAloApi(email);

    if (customerState.status === 'activation-required') {
      const normalizedEmail = email.trim().toLowerCase();
      setRecoveryPrefillEmail(normalizedEmail || email);
      setFormState((prevState) => ({
        ...prevState,
        FirstName: false,
        LastName: false,
        Email: Boolean(normalizedEmail),
        CreatePassword: false,
      }));
      setFormType('activate-account');
      setIsCheckingCustomerState(false);
      return;
    }

    if (customerState.status === 'error') {
      setFormError({
        type: 'recover',
        alertType: 'error',
        errorMessage: customerState.errorMessage || ACCOUNT_STATUS_LOOKUP_ERROR_MESSAGE,
      });
      setIsCheckingCustomerState(false);
      return;
    }

    // Continue with existing theme recover flow for non-disabled customers.
    fetch(prependLocaleToPath('/account/recover'), {
      method: 'POST',
      body: formData,
    })
      .then((res) => res.text())
      .then((response) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        const errorText = getErrorMessagingFromShopify(response);
        if (errorText) {
          recordCustomEventToNewRelic('reset_password_error', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: errorText,
            response: JSON.stringify(response),
          });
          setFormError({ type: 'recover', alertType: 'error', errorMessage: errorText });
        } else {
          setFormType('reset-confirmation');
          setResetPasswordEmail(email);
          recordCustomEventToNewRelic('reset_password', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: 'Successful Password Reset',
          });
          pushEventToGTMDataLayer({
            event: 'forget_password_form_submit',
            form: 'forget_password',
          });
        }
      })
      .catch((error) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        console.error(error);
        recordCustomEventToNewRelic('reset_password_error', {
          fetchDuration,
          userAgent: window.navigator.userAgent,
          date: Date.now(),
          pathname: window.location.pathname,
          message: error.message || 'Reset password fetch failed',
        });
      })
      .finally(() => {
        setIsCheckingCustomerState(false);
      });
  };

  const subscribeCustomerViaWiz = (email: string, tags?: string[]) => {
    if (!window?.wizUrl) return false;
    const startTime = performance.now();
    const wizEndpoint = `https://${window?.wizUrl}/offer-enroll`;
    const requestBody = {
      email: email,
      tags: tags || [],
      accepts_marketing: 1,
    };
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    };

    return fetch(wizEndpoint, options)
      .then((response) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        if (!response.ok) {
          console.log('Offer enroll error: ', 'Network response was not OK');
          recordCustomEventToNewRelic('subscribe_customer_via_wiz_error', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: 'Subscribe customer via WIZ failed: Network response was not OK - ' + JSON.stringify(response),
          });
          return false;
        }
        return response.json();
      })
      .then((data) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        if (data.status === 'success') {
          recordCustomEventToNewRelic('subscribe_customer_via_wiz', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: 'Successful subscribe customer via WIZ',
          });
          return true;
        } else {
          recordCustomEventToNewRelic('subscribe_customer_via_wiz_error', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: 'Subscribe customer via WIZ failed: ' + JSON.stringify(data),
          });
          return false;
        }
      });
  };

  const login = async (formData: FormData) => {
    if (isAuthorizing) return; // Prevent duplicate submissions
    const startTime = performance.now();
    setIsAuthorizing(true);
    const acceptsMarketing = !!formData.get('customer[accepts_marketing]')?.toString();
    const email = String(formData.get('customer[email]') || '');
    setFormError(null);
    fetch(prependLocaleToPath('/account/login'), {
      method: 'post',
      body: formData,
    })
      .then((res) => res.text())
      .then(async (response) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        const errorText = getErrorMessagingFromShopify(response);
        if (errorText) {
          setFormError({ type: 'login', alertType: 'error', errorMessage: errorText });
          recordCustomEventToNewRelic('login_error', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: errorText,
            response: JSON.stringify(response),
          });
        } else {
          FACE_ID.incrementUserLoggedInCount(email.toLowerCase());
          recordCustomEventToNewRelic('login', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: 'Successful Login',
          });
          // redirect to return URL + heap tracking
          fireHeapCustomerLoginEvent({ isSignInModal });
          if (acceptsMarketing && email.length > 0)
            await subscribeCustomerViaWiz(email, ['offer-subscribed-via-login-form']);
          await triggerMergeCartOnLogin(true);
          if (discountCodeToApplyOnSignIn) await applyDiscountCode(discountCodeToApplyOnSignIn);
          if (IS_CHECKOUT_PAGE) {
            window.location.href = _returnUrl;
          } else {
            window.location.href = `${_returnUrl}${_returnUrl.includes('?') ? '&' : '?'}auth_event=login`;
          }
        }
      })
      .catch((error) => {
        console.info({ error });
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        recordCustomEventToNewRelic('login_error', {
          fetchDuration,
          userAgent: window.navigator.userAgent,
          date: Date.now(),
          pathname: window.location.pathname,
          message: error.message || 'Login fetch failed',
        });
      })
      .finally(() => setIsAuthorizing(false)); //Reset value
  };

  const createAccount = async (formData: FormData) => {
    if (isAuthorizing) return; // Prevent duplicate submissions
    const startTime = performance.now();
    setIsAuthorizing(true);
    setFormError(null);
    // Attach customer tags for signup tracking
    try {
      const tagsToAdd: string[] = [];
      // Add hostname tag
      const hostname = window.location.hostname || '';
      if (hostname) {
        const normalized = hostname.toLowerCase().replace(/^www\./, '');
        tagsToAdd.push(normalized);
      }

      // Add RSVP tag if user signed up directly from RSVP modal on events page
      if (addRsvpTag()) {
        tagsToAdd.push(RSVP_COMMUNITY_TAG);
      }

      // Combine with existing tags and set once
      if (tagsToAdd.length > 0) {
        const existingTags = formData.get('customer[tags]')?.toString();
        const allTags = [existingTags, ...tagsToAdd].filter(Boolean).join(',');
        formData.set('customer[tags]', allTags);
      }
    } catch (err) {
      console.warn('Failed to attach customer tags:', err);
    }
    await fetch(prependLocaleToPath(`/account`), {
      method: 'post',
      body: formData,
    })
      .then((res) => res.text())
      .then(async (response) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        const errorText = getErrorMessagingFromShopify(response);
        const submittedEmail = String(formData.get('customer[email]') || '')
          .trim()
          .toLowerCase();
        const shouldShowSuccess = Boolean(
          errorText && submittedEmail && errorText.toLowerCase().includes(submittedEmail)
        );
        if (errorText) {
          const activationSuccessCopy = `${ACTIVATION_EMAIL_SENT_COPY_PREFIX} ${submittedEmail} to complete account activation.`;
          const signInActivationSuccessCopy = `Welcome back! ${activationSuccessCopy}`;
          setFormError({
            type: 'create',
            alertType: shouldShowSuccess ? 'success' : 'error',
            errorMessage: shouldShowSuccess
              ? isActivateAccountForm
                ? activationSuccessCopy
                : signInActivationSuccessCopy
              : errorText,
          });
          recordCustomEventToNewRelic('create_account_error', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: errorText,
            response: JSON.stringify(response),
          });
        } else {
          // redirect to return URL + heap tracking
          const customerPhone = formData.getAll('customer[note][phonenumber]')[0]?.toString();
          FACE_ID.incrementUserLoggedInCount(String(formData.get('customer[email]') || '').toLowerCase());
          recordCustomEventToNewRelic('create_account', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: 'Successful Sign Up',
          });
          // Can we count 3 characters as a valid phone number???
          const hasProvidedPhoneNumber = Boolean(customerPhone && customerPhone.length > 3);
          fireHeapCustomerCreateEvent({ hasProvidedPhoneNumber, isSignInModal });
          await triggerMergeCartOnLogin(true);
          if (discountCodeToApplyOnSignIn) await applyDiscountCode(discountCodeToApplyOnSignIn);
          if (IS_CHECKOUT_PAGE) {
            window.location.href = _returnUrl;
          } else {
            window.location.href = _returnUrl + '&auth_event=sign_up';
          }
        }
      })
      .catch((error) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        console.info({ error });
        recordCustomEventToNewRelic('create_account_error', {
          fetchDuration,
          userAgent: window.navigator.userAgent,
          date: Date.now(),
          pathname: window.location.pathname,
          message: error.message || 'Create Account fetch failed',
        });
      })
      .finally(() => setIsAuthorizing(false)); //Reset value
  };

  useDidUpdateEffect(() => {
    setFormError(null);
  }, [formType]);

  const [acceptsMarketing, setAcceptsMarketing] = useState(true);

  if (isLoading) return null;

  const activeFormData = activeFormRef.current ? Array.from(new FormData(activeFormRef.current).entries()) : [];
  // a workaround to Shopify checkout page(s) disabling all failed Submit buttons cause the user to be stuck
  const formKey = JSON.stringify(activeFormData) + formError?.errorMessage;

  const validateBirthdayInput = (value: string, isOutOfFocus = false) => {
    const pattern = /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])$/;
    if (value === '__/__' || value === '') {
      setBirthdayErrorState({ shouldShowErrorUI: false, hasValidValue: true });
    } else if (value.includes('_')) {
      setBirthdayErrorState({ hasValidValue: false, shouldShowErrorUI: isOutOfFocus ? true : false });
    } else {
      setBirthdayErrorState({ shouldShowErrorUI: !pattern.test(value), hasValidValue: pattern.test(value) });
    }
  };

  const loginEmailFormEvent = () => handleFormEvents('login_start', 'email');
  const signUpEmailFormEvent = () => handleFormEvents('sign_up_start', 'email');
  const { birthdayInputLabel } = signInModalSettings || {};

  const nameInput = (
    <div className="row no-gutters" style={{ gap: 'var(--spacing-sm)' }}>
      <div className="col">
        <div className="form-group form-group-placeholder">
          <FormInput
            options={{
              id: 'FirstName',
              wrapperClasses: '',
              type: 'text',
              required: true,
              errorText: 'Required',
              label: 'First Name',
              name: 'customer[first_name]',
              pattern: '^(?!\\s*$).+',
              defaultValue: '',
              handleFormState: (isValid) => {
                setFormState({ ...formState, FirstName: isValid });
              },
              maxLength: MAX_NAME_LENGTH,
              autocomplete: 'given-name',
              onChange: signUpEmailFormEvent,
            }}
          />
        </div>
      </div>
      <div className="col">
        <div className="form-group form-group-placeholder">
          <FormInput
            options={{
              id: 'LastName',
              wrapperClasses: '',
              type: 'text',
              required: true,
              errorText: 'Required',
              label: 'Last Name',
              name: 'customer[last_name]',
              pattern: '^(?!\\s*$).+',
              defaultValue: '',
              handleFormState: (isValid) => {
                setFormState({ ...formState, LastName: isValid });
              },
              maxLength: MAX_NAME_LENGTH,
              autocomplete: 'family-name',
              onChange: signUpEmailFormEvent,
            }}
          />
        </div>
      </div>
    </div>
  );

  const emailInput = (
    <div className="form-group form-group-placeholder">
      <FormInput
        options={{
          id: 'Email',
          wrapperClasses: '',
          type: 'email',
          required: true,
          errorText: 'Please enter a valid email',
          label: 'Email',
          pattern: globalWindow?.emailRegex,
          defaultValue: prePopulatedEmail,
          name: 'customer[email]',
          handleFormState: (isValid) => {
            if (formError !== null) {
              setFormError(null);
            }
            setFormState({ ...formState, Email: isValid });
          },
          onChange: signUpEmailFormEvent,
          autocomplete: 'email',
        }}
      />
    </div>
  );

  return (
    <>
      {heroVisibility ? heroBlock : null}
      <div className="auth-form-wrapper">
        {formType === 'sign-in' ? (
          <>
            {FormTypeSwitch}
            <form
              key="customer_login"
              ref={activeFormRef}
              id="customer_login"
              // eslint-disable-next-line no-script-url
              action="javascript:void(0);"
              onSubmit={(e) => {
                e.preventDefault();
                try {
                  login(new FormData(e.currentTarget));
                } catch (error) {
                  console.error('Error firing tracking events for Sign In', error);
                }
              }}
            >
              <input type="hidden" name="form_type" value="customer_login" />
              <input type="hidden" name="utf8" value="✓" />
              {signInCta ? (
                <p className="cta-title" dangerouslySetInnerHTML={{ __html: signInCta || '' }}></p>
              ) : (
                <div style={{ marginTop: '25px' }}></div>
              )}
              <div className="form-group" data-testid="login-form">
                <div className="form-group form-group-placeholder">
                  <FormInput
                    options={{
                      id: 'CustomerEmail',
                      wrapperClasses: '',
                      type: 'email',
                      required: true,
                      errorText: 'Please enter a valid email',
                      label: 'Email',
                      pattern: globalWindow?.emailRegex,
                      name: 'customer[email]',
                      defaultValue: prePopulatedEmail,
                      handleFormState: (isValid) => {
                        if (formError !== null) {
                          setFormError(null);
                        }
                        setCustomerEmailState(isValid);
                      },
                      onChange: loginEmailFormEvent,
                      autocomplete: 'email',
                    }}
                  />
                  <ErrorBoundary key={(!isLocal || new Date()).toString()}>
                    {FACE_ID.showFaceIdLoginButton() ? <FaceIdButton faceIdReturnUrl={_returnUrl} /> : null}
                  </ErrorBoundary>
                </div>
                <div className="form-group form-group-placeholder">
                  <FormInput
                    options={{
                      id: 'CustomerPassword',
                      wrapperClasses: '',
                      type: showPassword ? 'text' : 'password',
                      required: true,
                      errorText: 'Please enter your password',
                      label: 'Password',
                      pattern: '^(?!\\s*$).+',
                      name: 'customer[password]',
                      handleFormState: (isValid) => {
                        if (formError !== null) {
                          setFormError(null);
                        }
                        setCustomerPasswordState(isValid);
                      },
                      autocomplete: 'current-password',
                      onChange: loginEmailFormEvent,
                    }}
                  />
                  <div className="__password-toggle" onClick={() => setShowPassword(!showPassword)}>
                    {!showPassword ? (
                      <span className="alo-eye-icon"></span>
                    ) : (
                      <span className="alo-eye-slash-icon"></span>
                    )}
                  </div>
                </div>
                {formError && formError.type === 'login' && formError.errorMessage ? (
                  <FormAlert errorMessage={formError.errorMessage} alertType={formError.alertType}></FormAlert>
                ) : null}
              </div>
              <div className="forgot-password-link">
                <span
                  aria-label="Forgot password"
                  data-testid="forgot-password-link"
                  onClick={() => setFormType('recovery')}
                >
                  Forgot Password?
                </span>
              </div>

              <ButtonWithProgress
                options={{
                  type: 'submit',
                  showProgressAnimation: isAuthorizing,
                  cta: 'Sign in',
                  wrapperClasses: 'form-group',
                  buttonClasses: `button-primary btn-primary btn-block`,
                  disableShrink: true,
                  key: formKey,
                  disabled: !isLoginFormSubmittable || isAuthorizing,
                  testId: 'submit-button',
                }}
              />
              {signInModalSettings?.showNewsletterCheckboxForSignUpFrom && (
                <>
                  <Checkbox
                    options={{
                      id: 'SubscribeLoginForm',
                      label: 'Email me with exclusive drops and offers',
                      checked: acceptsMarketing,
                      name: 'customer[accepts_marketing]',
                      onChange: (event) => {
                        setAcceptsMarketing(event.currentTarget.checked);
                      },
                    }}
                  />
                  <span className="label-add" style={{ textAlign: 'left', paddingTop: 8 }}>
                    By joining, you agree to our{' '}
                    <a href="/pages/terms-of-use" target="_blank" className="terms-of-service-link">
                      Terms of Service
                    </a>{' '}
                    and to receive our email newsletter.
                  </span>
                </>
              )}
            </form>
            <OxiSocialLogin />
          </>
        ) : formType === 'create-account' || formType === 'activate-account' ? (
          <>
            {!isActivateAccountForm ? FormTypeSwitch : null}
            <form
              ref={activeFormRef}
              key={isActivateAccountForm ? 'activate_customer' : 'create_customer'}
              id={isActivateAccountForm ? 'activate_customer' : 'create_customer'}
              onSubmit={(e) => {
                e.preventDefault();
                try {
                  createAccount(new FormData(e.currentTarget));
                } catch (error) {
                  console.error('Error firing tracking events for Create Account', error);
                }
              }}
            >
              <input type="hidden" name="form_type" value="create_customer" />
              <input type="hidden" name="utf8" value="✓" />
              {isActivateAccountForm ? (
                <>
                  <h2 className="recovery-title">We Found Your Email On File</h2>
                  <p className="activate-account-subtitle">
                    Finish setting up your account to manage your orders and enjoy a faster checkout.
                  </p>
                </>
              ) : createAccountCta ? (
                <p className="cta-title" dangerouslySetInnerHTML={{ __html: createAccountCta || '' }}></p>
              ) : (
                <div style={{ marginTop: '25px' }}></div>
              )}
              <div className="form-group">
                {isActivateAccountForm ? emailInput : nameInput}
                {isActivateAccountForm ? nameInput : emailInput}

                <div className="form-group form-group-placeholder">
                  <FormInput
                    options={{
                      id: 'CreatePassword',
                      wrapperClasses: '',
                      type: showPassword ? 'text' : 'password',
                      required: true,
                      errorText: 'Must be at least 6 characters',
                      label: 'Password',
                      pattern: '^.{6,}$',
                      name: 'customer[password]',
                      handleFormState: (isValid) => {
                        if (formError !== null) {
                          setFormError(null);
                        }
                        setFormState({ ...formState, CreatePassword: isValid });
                      },
                      onChange: signUpEmailFormEvent,
                      autocomplete: 'new-password',
                    }}
                  />
                  <div className="__password-toggle" onClick={() => setShowPassword(!showPassword)}>
                    {!showPassword ? (
                      <span className="alo-eye-icon"></span>
                    ) : (
                      <span className="alo-eye-slash-icon"></span>
                    )}
                  </div>
                </div>
                {isInUS && !isActivateAccountForm ? (
                  <>
                    <div className="form-group form-group-placeholder">
                      <div className={`yoga-ui-text-input  ${phoneNumberError ? 'error-border' : ''}`}>
                        <input
                          type="tel"
                          name="customer[note][phonenumber]"
                          id="PhoneNumber"
                          className=" "
                          placeholder="Phone Number (optional)"
                          spellCheck="false"
                          autoComplete="tel"
                          autoCapitalize="off"
                          onChange={(e) => {
                            signUpEmailFormEvent();
                            phoneNumberFormatter(e);
                          }}
                          onBlur={(e) => {
                            const pattern = /^\(\d{3}\)\s\d{3}[- ]{1}\d{4}$/;
                            if (e.currentTarget.value === '') {
                              setPhoneNumberErrorState(false);
                            } else {
                              setPhoneNumberErrorState(!pattern.test(e.currentTarget.value));
                            }
                          }}
                          onFocus={(e) => {
                            setPhoneNumberErrorState(false);
                          }}
                        />
                        <label htmlFor="PhoneNumber" className="">
                          Phone Number
                        </label>
                        {phoneNumberError ? (
                          <span className="error error-input-form">Please enter a valid US number</span>
                        ) : null}
                        <MoreInfoTooltip text={phoneNumberTooltipMessage} />
                      </div>
                    </div>
                    {signInModalSettings?.shouldShowBirthdayInput && (
                      <div className="form-group form-group-placeholder">
                        {birthdayInputLabel ? <p className="birthday-input-description">{birthdayInputLabel}</p> : null}
                        <div className={`yoga-ui-text-input  ${birthdayError ? 'error-border' : ''}`}>
                          <InputMask
                            mask="99/99"
                            name="customer[note][birthday]"
                            spellCheck={false}
                            id="Birthday"
                            placeholder="MM/DD (optional)"
                            inputMode="numeric"
                            onBlur={(e) => validateBirthdayInput(e.currentTarget.value, true)}
                            onChange={(e) => {
                              signUpEmailFormEvent();
                              validateBirthdayInput(e.currentTarget.value);
                            }}
                            onFocus={() => {
                              // Hide Error UI on input focus
                              setBirthdayErrorState({ ...birthdayError, shouldShowErrorUI: false });
                            }}
                          />
                          <label htmlFor="Birthday" className="">
                            Birthday
                          </label>
                          {birthdayError.shouldShowErrorUI ? (
                            <span className="error error-input-form">Please enter a valid date</span>
                          ) : null}
                          <MoreInfoTooltip text="Get a special Alo treat on your birthday!" />
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
              {formError && formError.type === 'create' && formError.errorMessage ? (
                <FormAlert errorMessage={formError.errorMessage} alertType={formError.alertType}></FormAlert>
              ) : null}
              <ButtonWithProgress
                options={{
                  type: 'submit',
                  showProgressAnimation: isAuthorizing,
                  cta: isActivateAccountForm ? 'Activate Account' : 'Join Now',
                  wrapperClasses: 'form-group',
                  buttonClasses: `button-primary btn-primary btn-block`,
                  disableShrink: true,
                  key: formKey,
                  disabled: !isAccountFormSubmittable || isAuthorizing,
                }}
              />
            </form>
            {isActivateAccountForm ? (
              <p className="activate-account-help">
                Need help?{' '}
                <a href="/pages/contact-us" style={{ textDecoration: 'underline' }}>
                  Contact us
                </a>
              </p>
            ) : (
              <span className="label-add">
                By joining, you agree to our{' '}
                <a href="/pages/terms-of-use" target="_blank" className="terms-of-service-link">
                  Terms of Service
                </a>{' '}
                and to receive email and mobile marketing messages from ALO.
              </span>
            )}
          </>
        ) : formType === 'reset-confirmation' ? (
          <div className="reset-email-sent-confirmation">
            <h2>Reset Email Sent</h2>
            <p style={{ margin: '30px 15px' }}>
              If <span className="email GlobalLinkNoTx">{resetPasswordEmail}</span> is associated with a valid account,
              you will receive password reset instructions via email.
            </p>
            <p style={{ margin: '20px 0' }}>
              Having trouble?{' '}
              <a href="/pages/contact-us" style={{ textDecoration: 'underline' }}>
                Contact us
              </a>
            </p>
          </div>
        ) : (
          <>
            <form
              ref={activeFormRef}
              key="forgot_password"
              id="ModalRecoverPasswordForm"
              onSubmit={(event) => submitResetPassword(event)}
            >
              <input type="hidden" name="form_type" value="recover_customer_password" />
              <input type="hidden" name="utf8" value="✓" />

              <h2 className="recovery-title">Forgot Password?</h2>
              <p style={{ margin: '24px' }}>
                Please enter your email address to receive a link to reset your password.
              </p>
              <div className="form-group form-group-collapse">
                <div className="form-group form-group-placeholder">
                  <FormInput
                    options={{
                      id: 'RecoverEmail',
                      wrapperClasses: '',
                      type: 'email',
                      required: true,
                      errorText: 'Please enter a valid email',
                      label: 'Email',
                      pattern: globalWindow?.emailRegex,
                      defaultValue: prePopulatedEmail,
                      name: 'email',
                      handleFormState: (isValid) => {
                        if (formError !== null) {
                          setFormError(null);
                        }
                        setFormState({ ...formState, RecoverEmail: isValid });
                      },
                      onChange: () => {
                        handleFormEvents('forget_password_form_start');
                      },
                    }}
                  />
                </div>
              </div>
              {formError && formError.type === 'recover' && formError.errorMessage ? (
                <FormAlert errorMessage={formError.errorMessage} alertType={formError.alertType}></FormAlert>
              ) : null}
              <div className="form-group">
                <button
                  type="submit"
                  className="button-primary btn btn-primary btn-block"
                  value="Reset Password"
                  key={formKey}
                  disabled={!isResetPasswordSubmittable || isCheckingCustomerState}
                >
                  Reset Password
                </button>
              </div>
              <div>
                <p>
                  Having trouble?{' '}
                  <a href="/pages/contact-us" style={{ textDecoration: 'underline' }}>
                    Contact us
                  </a>
                </p>
                <p>
                  Don't have an account?{' '}
                  <span
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => setFormType('create-account')}
                  >
                    Join now
                  </span>
                </p>
              </div>
            </form>
          </>
        )}
      </div>
    </>
  );
};

export default AuthForm;

const OxiSocialLogin = React.memo(() => {
  // if origin contains aloyoga.com redirect to aloyoga.com else alo.com
  const parenturl =
    window.location.origin === 'https://www.alo.com' ? 'https://www.alo.com' : 'https://www.aloyoga.com';
  return (
    <div className="login-item social-login">
      <h6 className="title h6 bold uppercase">One-Click Sign In</h6>
      <div className="form-group" style={{ marginTop: '0.5rem' }}>
        <div className="oxi-social-login">
          <iframe
            onClick={(e) => console.log(e)}
            onClickCapture={(e) => console.log(e)}
            id="social_login_frame"
            title="Social Login Frame"
            // ? [REVISIT] pass through
            // ? [REVISIT] rewrite this & test dynamic/anywhere returnTo with all 3 providers (which might force allow listed return urls)
            // src={`https://social-login.oxiapps.com/widget?site=alo-yoga.myshopify.com&vt=928382&parenturl=https://www.aloyoga.com/account/login?return_url=%2Faccount`}
            // ? strictly making use of the 'social-login-redirect' fallback/else case
            src={`https://social-login.oxiapps.com/widget?site=alo-yoga.myshopify.com&vt=928382&parenturl=${parenturl}/account/login`}
            scrolling="no"
          />
        </div>
      </div>
    </div>
  );
});

// -- our TS version isn't setup to be aware of URL.canParse
function newURL(url: string, base?: string | URL | undefined): URL | null {
  try {
    return new URL(url, base);
  } catch (error) {
    return null;
  }
}

function parseUrl(inputString: string): URL {
  let result: URL | null;
  result = newURL(inputString);
  if (result) return result; // base case
  result = newURL(inputString, window.location.origin);
  if (result) return result; // relative path

  throw new Error('Failed to parse redirect URL: ' + inputString);
}

const MoreInfoTooltip = ({ text }: { text: string }) => {
  return (
    <div className="more-info-tooltip">
      <div className="more-info-tooltip-icon">
        <img src="https://cdn.shopify.com/s/files/1/2185/2813/files/icon.svg?v=1675189680" alt="More info" />
      </div>
      <span className="tooltip-text">{text}</span>
    </div>
  );
};

const getErrorMessagingFromShopify = (response: string) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(response, 'text/html');
    const errorDiv = doc.querySelector('div.errors li') || doc.querySelector('div.errors');

    if (!errorDiv) {
      return;
    }

    // decode HTML before sanitizing
    const ta = document.createElement('textarea');
    ta.innerHTML = errorDiv.innerHTML;

    return sanitizeHtml(ta.value);
  } catch (err) {
    console.error('Error parsing Shopify error response:', err);
    return 'An error occurred';
  }
};

interface FormError {
  type: 'create' | 'login' | 'recover';
  alertType: FormAlertType;
  errorMessage: string;
}
